import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import MetricTooltip from '../../components/MetricTooltip';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip, Legend, Title
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, Title);

const ABTestingDashboard = () => {
  const [experiments, setExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [experimentData, setExperimentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveExperiments();
  }, []);

  useEffect(() => {
    if (selectedExperiment) {
      fetchExperimentData(selectedExperiment);
    }
  }, [selectedExperiment]);

  const fetchActiveExperiments = async () => {
    try {
      console.log('🧪 [A/B Dashboard] Fetching active experiments...');
      const response = await apiService.abTesting.getActiveExperiments();
      console.log('🧪 [A/B Dashboard] Raw response:', response);
      console.log('🧪 [A/B Dashboard] response.data:', response.data);
      console.log('🧪 [A/B Dashboard] response.data?.experiments:', response.data?.experiments);
      console.log('🧪 [A/B Dashboard] Type of experiments:', typeof response.data?.experiments, Array.isArray(response.data?.experiments));
      
      const expList = response.data?.experiments || [];
      console.log('🧪 [A/B Dashboard] Setting experiments:', expList.length, 'items');
      setExperiments(expList);
      
      if (expList.length > 0) {
        console.log('🧪 [A/B Dashboard] Selecting first experiment:', expList[0].key);
        setSelectedExperiment(expList[0].key);
      } else {
        console.warn('🧪 [A/B Dashboard] No active experiments found');
      }
    } catch (err) {
      console.error('🧪 [A/B Dashboard] ❌ Error fetching experiments:', err);
      console.error('🧪 [A/B Dashboard] Error response:', err.response?.status, err.response?.data);
      setError('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  };

  const fetchExperimentData = async (experimentKey) => {
    setLoading(true);
    try {
      console.log('🧪 [A/B Dashboard] Fetching data for experiment:', experimentKey);
      
      const results = await Promise.allSettled([
        apiService.abTesting.getProgress(experimentKey),
        apiService.abTesting.getSignificance(experimentKey, 'quality_score'),
        apiService.abTesting.getEffectSize(experimentKey, 'quality_score'),
        apiService.abTesting.getConfidenceIntervals(experimentKey, 'quality_score'),
        apiService.abTesting.getMonitoring(experimentKey)
      ]);

      const labels = ['progress', 'significance', 'effectSize', 'confidenceIntervals', 'monitoring'];
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          console.log(`🧪 [A/B Dashboard] ✅ ${labels[i]}:`, result.value?.data);
        } else {
          console.error(`🧪 [A/B Dashboard] ❌ ${labels[i]} failed:`, result.reason?.response?.status, result.reason?.message);
        }
      });

      setExperimentData({
        progress: results[0].status === 'fulfilled' ? results[0].value.data : null,
        significance: results[1].status === 'fulfilled' ? results[1].value.data : null,
        effectSize: results[2].status === 'fulfilled' ? results[2].value.data : null,
        confidenceIntervals: results[3].status === 'fulfilled' ? results[3].value.data : null,
        monitoring: results[4].status === 'fulfilled' ? results[4].value.data : null
      });
      
      console.log('🧪 [A/B Dashboard] ✅ All experiment data loaded');
    } catch (err) {
      console.error('🧪 [A/B Dashboard] ❌ Error fetching experiment data:', err);
      setError('Failed to load experiment data');
    } finally {
      setLoading(false);
    }
  };

  const abIcons = [
    { emoji: '🧪', size: '4xl', opacity: '15' },
    { emoji: '📈', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '🔬', size: '4xl', opacity: '15' },
    { emoji: '💎', size: '4xl', opacity: '15' }
  ];

  const getExperimentIcon = (key) => key?.toLowerCase().includes('moodboard') ? '🎨' : '🪄';

  if (loading && !experimentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 rounded-full shadow-2xl mb-6 animate-bounce">
            <span className="text-3xl animate-pulse">🧪</span>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-serif italic">Loading A/B testing dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !experiments.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-red-200/50 text-center">
          <span className="text-5xl mb-4 block">⚠️</span>
          <p className="text-red-600 font-semibold text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const { progress, significance, effectSize, confidenceIntervals, monitoring } = experimentData || {};
  const currentExperiment = experiments.find(e => e.key === selectedExperiment);
  const variantNames = currentExperiment?.variants || {};
  const getVariantLabel = (v) => variantNames[v]?.name ? `${v} - ${variantNames[v].name}` : `Variant ${v}`;
  const getVariantTipKey = (v) => {
    const name = (variantNames[v]?.name || '').toLowerCase().replace(/[^a-z]/g, '_');
    const prefix = selectedExperiment?.includes('DREAM') ? 'dream' : 'moodboard';
    const map = { few_shot: 'few_shot', context_aware: 'context_aware', hybrid_reasoning: 'hybrid_reasoning', theatrical: 'theatrical', professional: 'professional' };
    return `${prefix}_${Object.keys(map).find(k => name.includes(k.replace('_', ''))) || name}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={abIcons} />

      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-12 relative">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">🧪</span>
          </div>

          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mb-6 tracking-wide">
            A/B Testing
          </h1>

          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">🔬</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>

          <p className="text-xl text-gray-700 font-light italic mb-8">
            Experiment results & statistical analysis ✨
          </p>

          {/* Experiment Selector */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {(Array.isArray(experiments) ? experiments : []).map((exp) => {
              const isSelected = selectedExperiment === exp.key;
              const sampleCount = isSelected ? (experimentData?.progress?.current_samples || 0) : 0;
              const progressPct = isSelected ? (experimentData?.progress?.progress_percent || experimentData?.progress?.progress_percentage || 0) : 0;
              const icon = getExperimentIcon(exp.key);
              return (
                <button
                  key={exp.key}
                  onClick={() => setSelectedExperiment(exp.key)}
                  className={`group/tab relative overflow-hidden rounded-xl font-bold transition-all duration-500 transform ${
                    isSelected
                      ? 'scale-105 shadow-xl shadow-purple-400/30'
                      : 'hover:scale-105 hover:shadow-lg shadow-md'
                  }`}
                >
                  <div className={`p-[2px] rounded-xl ${isSelected ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500' : 'bg-gradient-to-r from-gray-200 to-gray-300 group-hover/tab:from-purple-300 group-hover/tab:to-pink-300'}`}>
                    <div className={`rounded-[10px] px-4 py-3 ${isSelected ? 'bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600' : 'bg-white/95 backdrop-blur-lg group-hover/tab:bg-white/80'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xl transition-transform duration-300 group-hover/tab:scale-110 ${isSelected ? 'bg-white/20' : 'bg-gradient-to-br from-purple-100 to-pink-100'}`}>
                          {icon}
                        </div>
                        <div className="text-left">
                          <div className={`text-sm font-serif font-bold ${isSelected ? 'text-white' : 'text-gray-800'}`}>{exp.name}</div>
                          <div className={`text-[11px] font-medium ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                            {isSelected ? `${sampleCount} samples collected` : 'Click to view results'}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-2">
                          <div className="h-1 rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-white/70 via-white/90 to-white/70 transition-all duration-1000" style={{ width: `${progressPct}%` }} />
                          </div>
                          <p className="text-[9px] text-white/60 mt-0.5 text-right">{Math.round(progressPct)}% complete</p>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

      {experimentData && (
        <>
          {/* Experiment Progress */}
          <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-purple-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
            <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
              <span className="text-3xl mr-3">🧪</span>
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Experiment Progress</span><MetricTooltip metricKey="experiment_progress" color="purple" />
            </h2>
            {/* Progress Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 text-center border border-purple-200/50">
                <span className="text-2xl block mb-2">🏷️</span>
                <p className="text-sm text-gray-500 font-medium">Status</p>
                <p className="text-xl font-bold text-purple-600 capitalize">{progress?.status || 'N/A'}</p>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-5 text-center border border-pink-200/50">
                <span className="text-2xl block mb-2">📈</span>
                <p className="text-sm text-gray-500 font-medium">Progress</p>
                <p className="text-xl font-bold text-pink-600">{progress?.progress_percent || progress?.progress_percentage || 0}%</p>
              </div>
              <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-5 text-center border border-rose-200/50">
                <span className="text-2xl block mb-2">🎯</span>
                <p className="text-sm text-gray-500 font-medium">Samples<MetricTooltip metricKey="sample_size" color="rose" /></p>
                <p className="text-xl font-bold text-rose-600">{progress?.current_samples || 0} / {progress?.target_sample_size || progress?.target_samples || '?'}</p>
              </div>
            </div>
            {/* Animated Progress Bar */}
            <div className="w-full bg-gray-100 rounded-full h-5 mb-6 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 h-full rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                style={{ width: `${Math.max(progress?.progress_percent || progress?.progress_percentage || 0, 3)}%` }}
              >
                <span className="text-white text-xs font-bold">{progress?.progress_percent || progress?.progress_percentage || 0}%</span>
              </div>
            </div>
            {/* Variant Split Chart + Table */}
            {progress?.samples_by_variant && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-50/50 to-pink-50/50 rounded-2xl p-5 border border-purple-200/30">
                  <h3 className="text-sm font-bold text-purple-500 uppercase tracking-wider mb-3">Variant Distribution<MetricTooltip metricKey="variant_distribution" color="purple" /></h3>
                  <div className="max-w-[220px] mx-auto">
                    <Doughnut
                      data={{
                        labels: Object.keys(progress.samples_by_variant).map(v => variantNames[v]?.name || `Variant ${v}`),
                        datasets: [{
                          data: Object.values(progress.samples_by_variant).map(d => typeof d === 'object' ? d.count : d),
                          backgroundColor: ['rgba(168,85,247,0.7)', 'rgba(236,72,153,0.7)', 'rgba(244,63,94,0.7)', 'rgba(99,102,241,0.7)'],
                          borderWidth: 2, borderColor: '#fff',
                        }]
                      }}
                      options={{ responsive: true, plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, font: { size: 11 } } } } }}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50">
                        <th className="px-4 py-3 text-left text-sm font-bold text-purple-600 uppercase tracking-wider">Variant</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-purple-600 uppercase tracking-wider">Prompt</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-purple-600 uppercase tracking-wider">Samples</th>
                        <th className="px-4 py-3 text-left text-sm font-bold text-purple-600 uppercase tracking-wider">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(progress.samples_by_variant).map(([variant, data]) => (
                        <tr key={variant} className="border-b border-purple-100/50 hover:bg-purple-50/30 transition-colors duration-200">
                          <td className="px-4 py-3 font-serif font-bold text-gray-800">{variant}</td>
                          <td className="px-4 py-3 text-gray-600">{variantNames[variant]?.name || '-'}<MetricTooltip metricKey={getVariantTipKey(variant)} color="purple" /></td>
                          <td className="px-4 py-3">
                            <span className="px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md">
                              {typeof data === 'object' ? data.count : data}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{typeof data === 'object' ? data.percentage : ((data / progress.current_samples) * 100).toFixed(1) + '%'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Statistical Significance */}
          <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-indigo-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
            <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
              <span className="text-3xl mr-3">📐</span>
              <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">Statistical Significance (Quality Score)</span><MetricTooltip metricKey="statistical_significance" color="indigo" />
            </h2>
            {/* Significance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(significance?.comparisons || {}).map(([key, comp]) => {
                const isSignificant = comp?.isSignificant;
                const pValue = comp?.pValue;
                return (
                  <div key={key} className={`rounded-2xl p-5 border-2 transition-all duration-300 hover:scale-105 ${
                    isSignificant
                      ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300/50 shadow-lg shadow-green-100'
                      : 'bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200/50'
                  }`}>
                    <div className="text-sm font-bold text-gray-500 mb-2">
                      {key.split('_vs_').map(v => getVariantLabel(v)).join(' vs ')}
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{isSignificant ? '✅' : '⏳'}</span>
                      <div>
                        <p className={`text-lg font-bold ${isSignificant ? 'text-green-600' : 'text-gray-600'}`}>
                          {comp?.significance || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-400">significance level<MetricTooltip metricKey="significance_level" color="indigo" /></p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-xs">T-Stat<MetricTooltip metricKey="t_statistic" color="indigo" /></p>
                        <p className="font-bold text-gray-700">{comp?.tStatistic?.toFixed(3) || 'N/A'}</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-2 text-center">
                        <p className="text-gray-400 text-xs">P-Value<MetricTooltip metricKey="p_value" color="indigo" /></p>
                        <p className={`font-bold ${pValue < 0.05 ? 'text-green-600' : pValue < 0.1 ? 'text-yellow-600' : 'text-gray-600'}`}>
                          {pValue?.toFixed(4) || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Effect Size */}
          <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-cyan-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
            <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
              <span className="text-3xl mr-3">📏</span>
              <span className="bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">Effect Size (Cohen's d)</span><MetricTooltip metricKey="cohens_d" color="teal" />
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(effectSize?.comparisons || {}).map(([key, comp]) => {
                const d = Math.abs(comp?.cohensD || 0);
                const interp = comp?.interpretation || 'N/A';
                const interpColor = interp === 'large' ? 'text-red-600' : interp === 'medium' ? 'text-yellow-600' : interp === 'small' ? 'text-blue-600' : 'text-gray-500';
                const gaugeWidth = Math.min(d / 1.5 * 100, 100);
                const gaugeColor = interp === 'large' ? 'from-red-400 to-red-500' : interp === 'medium' ? 'from-yellow-400 to-orange-500' : 'from-blue-400 to-cyan-500';
                return (
                  <div key={key} className="bg-gradient-to-br from-cyan-50/50 to-teal-50/50 rounded-2xl p-5 border border-cyan-200/50 hover:scale-105 transition-all duration-300">
                    <div className="text-sm font-bold text-gray-500 mb-3">
                      {key.split('_vs_').map(v => getVariantLabel(v)).join(' vs ')}
                    </div>
                    <div className="text-center mb-3">
                      <span className="text-3xl font-bold text-gray-800">{comp?.cohensD?.toFixed(3) || 'N/A'}</span>
                      <p className={`text-sm font-bold ${interpColor} uppercase mt-1`}>{interp}<MetricTooltip metricKey="effect_interpretation" color="teal" /></p>
                    </div>
                    {/* Gauge Bar */}
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden mb-2">
                      <div className={`bg-gradient-to-r ${gaugeColor} h-full rounded-full transition-all duration-1000`} style={{ width: `${gaugeWidth}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>0 (none)</span>
                      <span>0.8+ (large)</span>
                    </div>
                    {comp?.direction && (
                      <p className="text-xs text-gray-500 mt-2 text-center">Direction: <span className="font-bold">{comp.direction}</span></p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Confidence Intervals */}
          <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-emerald-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
            <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
              <span className="text-3xl mr-3">🎯</span>
              <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">Confidence Intervals (95%)</span><MetricTooltip metricKey="confidence_intervals" color="emerald" />
            </h2>
            <div className="space-y-4">
              {Object.entries(confidenceIntervals?.comparisons || {}).map(([key, comp]) => {
                const lower = comp?.lowerBound || 0;
                const upper = comp?.upperBound || 0;
                const diff = comp?.difference || 0;
                const includesZero = comp?.includesZero;
                const range = Math.max(Math.abs(lower), Math.abs(upper)) * 2 || 1;
                const centerPct = 50;
                const lowerPct = Math.max(centerPct + (lower / range) * 40, 5);
                const upperPct = Math.min(centerPct + (upper / range) * 40, 95);
                return (
                  <div key={key} className={`rounded-2xl p-5 border-2 ${includesZero ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200/50' : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200/50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-gray-600">{key.split('_vs_').map(v => getVariantLabel(v)).join(' vs ')}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${includesZero ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                        {includesZero ? '⚠️ Includes Zero' : '✅ Significant'}<MetricTooltip metricKey={includesZero ? 'ci_includes_zero' : 'ci_significant'} color={includesZero ? 'yellow' : 'emerald'} />
                      </span>
                    </div>
                    {/* Visual Range Bar */}
                    <div className="relative w-full h-8 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div className="absolute top-0 h-full w-px bg-gray-400" style={{ left: '50%' }}></div>
                      <div className={`absolute top-1 bottom-1 rounded-full ${includesZero ? 'bg-gradient-to-r from-yellow-300 to-orange-300' : 'bg-gradient-to-r from-green-300 to-emerald-400'}`}
                        style={{ left: `${lowerPct}%`, right: `${100 - upperPct}%` }}
                      ></div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-sm">
                      <div><p className="text-gray-400 text-xs">Difference</p><p className="font-bold text-gray-700">{diff?.toFixed(3)}</p></div>
                      <div><p className="text-gray-400 text-xs">Lower</p><p className="font-bold text-red-500">{lower?.toFixed(3)}</p></div>
                      <div><p className="text-gray-400 text-xs">Upper</p><p className="font-bold text-green-600">{upper?.toFixed(3)}</p></div>
                      <div><p className="text-gray-400 text-xs">Margin</p><p className="font-bold text-gray-600">{comp?.marginOfError?.toFixed(3) || 'N/A'}</p></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Monitoring / Health */}
          {monitoring && (
            <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-rose-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
              <h2 className="text-2xl font-serif font-bold mb-6 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
                <span className="text-3xl mr-3">🩺</span>
                <span className="bg-gradient-to-r from-rose-600 to-red-600 bg-clip-text text-transparent">Experiment Health</span><MetricTooltip metricKey="experiment_health" color="rose" />
              </h2>
              <div className="text-center mb-6">
                <span className={`inline-flex items-center gap-2 px-8 py-3 rounded-full text-lg font-bold shadow-lg ${
                  monitoring.health === 'healthy'
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-green-200'
                    : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-yellow-200'
                }`}>
                  {monitoring.health === 'healthy' ? '✅ Healthy' : '⚠️ Issues Detected'}
                </span>
              </div>

              {monitoring.alerts?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {monitoring.alerts.map((alert, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 rounded-r-xl p-4 text-red-700 font-medium">
                      🚨 {alert}
                    </div>
                  ))}
                </div>
              )}

              {monitoring.warnings?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {monitoring.warnings.map((warning, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-r-xl p-4 text-yellow-700 font-medium">
                      ⚠️ {warning}
                    </div>
                  ))}
                </div>
              )}

              {monitoring.health === 'healthy' && !monitoring.alerts?.length && !monitoring.warnings?.length && (
                <p className="text-center text-gray-500 italic">No issues detected. Experiment is running smoothly. 🎉</p>
              )}
            </section>
          )}
        </>
      )}

      </div>
    </div>
  );
};

export default ABTestingDashboard;
